/**
 * Scheduled: booked posts by day. Move shifts a post to the next free slot after its current one;
 * unschedule returns it to Awaiting approval. Until Metricool connects, Publish marks the post published
 * (the formal record) and hands over the video and caption to post by hand (2026-10-04).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PLATFORM_RULES, type Platform } from '@studio/contracts';
import { RowMenu } from '@/components/row-menu';
import { TopBar } from '@/components/shell';
import { queryClient, trpc } from '@/lib/trpc';

export function ScheduledPage() {
  const queue = useQuery(trpc.posts.queue.queryOptions({ status: 'scheduled' }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.posts.queue.queryKey({ status: 'scheduled' }) });
  const update = useMutation(trpc.posts.update.mutationOptions({ onSuccess: refresh }));
  const sendBack = useMutation(trpc.posts.sendBack.mutationOptions({ onSuccess: refresh }));
  const [published, setPublished] = useState<{ videoUrl: string; text: string; file: string } | null>(null);
  const publish = useMutation(trpc.posts.publish.mutationOptions({
    onSuccess: (r) => {
      setPublished({
        videoUrl: r.videoUrl,
        text: [r.caption, r.belowHook ?? '', r.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')].filter(Boolean).join('\n\n'),
        file: `${r.platform}-${new Date().toISOString().slice(0, 10)}.mp4`,
      });
      void refresh();
      void queryClient.invalidateQueries({ queryKey: trpc.posts.queue.queryKey({ status: 'published' }) });
    },
  }));

  const byDay = new Map<string, NonNullable<typeof queue.data>['posts']>();
  for (const p of queue.data?.posts ?? []) {
    const day = p.slotAt ? new Date(p.slotAt).toDateString() : 'Unbooked';
    byDay.set(day, [...(byDay.get(day) ?? []), p]);
  }

  return (
    <>
      <TopBar title="Scheduled">
        <span className="sub crumb">{queue.data?.posts.length ?? 0} booked · Metricool push deferred, slots hold until it connects</span>
      </TopBar>
      <section className="content col">
        {[...byDay.entries()].map(([day, posts]) => (
          <div key={day} className="sched-day">
            <div className="label">{day}</div>
            <div className="card lib-table">
              <table className="tbl">
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id}>
                      <td className="mono sched-time">{p.slotAt ? new Date(p.slotAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}</td>
                      <td><span className="chip">{PLATFORM_RULES[p.platform as Platform]?.label ?? p.platform}</span></td>
                      <td className="sched-caption">{p.caption.slice(0, 80)}</td>
                      <td className="n nowrap">
                        <button type="button" className="btn primary" disabled={publish.isPending} onClick={() => publish.mutate({ postId: p.id })}>Publish</button>
                      </td>
                      <td className="n">
                        <RowMenu items={[
                          { label: 'Move a day later', disabled: update.isPending, onClick: () => { if (p.slotAt) update.mutate({ postId: p.id, slotAt: new Date(new Date(p.slotAt).getTime() + 86400000).toISOString() }); } },
                          { label: 'Move a day earlier', disabled: update.isPending, onClick: () => { if (p.slotAt) update.mutate({ postId: p.id, slotAt: new Date(new Date(p.slotAt).getTime() - 86400000).toISOString() }); } },
                          null,
                          { label: 'Unschedule', danger: true, disabled: sendBack.isPending, onClick: () => sendBack.mutate({ postId: p.id, reason: 'Unscheduled from the calendar' }) },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {queue.data?.posts.length === 0 && <div className="empty">Nothing booked. Approved posts land here at their slots.</div>}
        {publish.isError && <div className="card pad error" role="alert">{publish.error.message}</div>}
      </section>
      {published && <PublishedDialog {...published} onClose={() => setPublished(null)} />}
    </>
  );
}

/** After Publish: the video to download and the caption to paste, for posting by hand. */
function PublishedDialog({ videoUrl, text, file, onClose }: { videoUrl: string; text: string; file: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The bucket is another origin, where a link's download attribute is ignored: fetched, it saves under a name.
  const download = async () => {
    try {
      const res = await fetch(videoUrl, { mode: 'cors' });
      if (!res.ok) throw new Error(`${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = file;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`The video would not download (${(err as Error).message}); open it instead.`);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      setError(`Could not copy (${(err as Error).message}); select the text instead.`);
    }
  };
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal publish-modal" role="dialog" aria-label="Published">
        <h2>Published</h2>
        <label className="field">
          <span className="label">Caption</span>
          <textarea className="publish-caption" readOnly value={text} rows={6} />
        </label>
        {error && <div className="error" role="alert">{error} <a className="link-btn" href={videoUrl} target="_blank" rel="noreferrer">Open the video</a></div>}
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Close</button>
          <div className="row">
            <button type="button" className="btn" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy caption'}</button>
            <button type="button" className="btn primary" onClick={() => void download()}>Download video</button>
          </div>
        </div>
      </div>
    </>
  );
}
